import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, ArrowUpCircle } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';

const OverdueTasksWidget = ({ viewType, userId }) => {
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchOverdueTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardService.getOverdueTasks(viewType);
      setOverdueTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching overdue tasks:', error);
      setOverdueTasks([]);
    } finally {
      setLoading(false);
    }
  }, [viewType]);

  useEffect(() => {
    fetchOverdueTasks();
  }, [fetchOverdueTasks]);

  const handleTaskClick = (task) => {
    // Navigate directly to task detail page
    navigate(`/tasks/${task.id}`);
  };

  const handleViewAllClick = () => {
    // Navigate to a tasks view or cases with overdue filter
    navigate('/cases?filter=overdue');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDaysOverdue = (days) => {
    if (days === 0) return 'Due today';
    if (days === 1) return '1 day overdue';
    return `${days} days overdue`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border">
      {/* Widget Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            {viewType === 'my' ? 'My Overdue Tasks' : 'Firm Overdue Tasks'}
            <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
              {overdueTasks.length}
            </span>
          </h3>
          {overdueTasks.length > 0 && (
            <button 
              onClick={handleViewAllClick}
              className="text-yellow-600 hover:text-yellow-700 text-sm font-medium"
            >
              View All
            </button>
          )}
        </div>
      </div>

      {/* Widget Content */}
      <div className="p-6">
        {overdueTasks.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-600 mb-3">
              <ArrowUpCircle className="h-12 w-12 mx-auto" />
            </div>
            <p className="text-gray-600 font-medium">All caught up!</p>
            <p className="text-gray-500 text-sm">No overdue tasks at the moment.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {overdueTasks.slice(0, 5).map((task) => (
              <div
                key={task.id}
                onClick={() => handleTaskClick(task)}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-blue-300 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-medium text-gray-900">{task.title}</h4>
                      {task.court_deadline && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Court Deadline
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">
                      {task.case_number} - {task.case_title}
                    </p>

                    {viewType === 'firm' && task.assigned_to && (
                      <div className="text-sm text-gray-500 mb-2">
                        <div>Assigned to: {task.assigned_to}</div>
                        {task.assigned_to_email && (
                          <div className="text-xs text-gray-400">{task.assigned_to_email}</div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center space-x-4 text-sm">
                      <span className="flex items-center text-red-600">
                        <Clock className="h-4 w-4 mr-1" />
                        {formatDaysOverdue(task.days_overdue)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                        {task.priority.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {overdueTasks.length > 5 && (
              <div className="text-center pt-4 border-t border-gray-200">
                <button 
                  onClick={handleViewAllClick}
                  className="text-yellow-600 hover:text-yellow-700 text-sm font-medium"
                >
                  View {overdueTasks.length - 5} more overdue tasks
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OverdueTasksWidget;